import * as React from 'react'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescriptionHidden,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { SearchableSelect } from '@/components/custom/searchable-select'
import {
  useContact,
  useCreateContact,
  useUpdateContact,
} from '@/hooks/use-contacts'
import { useCompaniesDropdownSearch } from '@/hooks/use-reference-data'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useCanEditCompany } from '@/hooks/use-permissions'
import { showSuccess, showError } from '@/lib/toast'
import {
  addContactSchema,
  type AddContactFormValues,
  type ContactTitle,
  JOB_TITLE_OPTIONS,
} from '@/lib/schemas/contact.schema'

interface AddContactModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: 'create' | 'edit'
  contactId?: string
  fixedCompanyId?: string
  initialEditCompanyId?: string
  initialEditTitle?: ContactTitle
  forcePrimaryContact?: boolean
  onSuccess?: () => void
}

export function AddContactModal({
  open,
  onOpenChange,
  mode = 'create',
  contactId,
  fixedCompanyId,
  initialEditCompanyId,
  initialEditTitle,
  forcePrimaryContact = false,
  onSuccess,
}: AddContactModalProps) {
  const isEditMode = mode === 'edit'
  const form = useForm<AddContactFormValues>({
    resolver: standardSchemaResolver(addContactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyId: '',
      title: '',
      notes: '',
    },
  })

  const watchedCompanyId = form.watch('companyId')
  const effectiveCompanyId = fixedCompanyId || watchedCompanyId

  const [companySearch, setCompanySearch] = React.useState('')
  const debouncedCompanySearch = useDebouncedValue(companySearch, 300)
  const canEditCompany = useCanEditCompany()

  const { data: companiesData, isLoading: companiesLoading } =
    useCompaniesDropdownSearch({
      search: debouncedCompanySearch,
      enabled: open,
    })

  const { data: contactData, isLoading: isLoadingContact } = useContact(
    contactId ?? '',
    open && isEditMode && Boolean(contactId)
  )

  const activeAssignment = React.useMemo(() => {
    if (!contactData?.assignments?.length) return undefined

    if (fixedCompanyId) {
      return contactData.assignments.find((a) => a.companyId === fixedCompanyId)
    }

    return contactData.assignments[0]
  }, [contactData?.assignments, fixedCompanyId])

  const fixedCompanyDisplayName = React.useMemo(() => {
    if (!fixedCompanyId) return ''
    return (
      companiesData?.data.find((c) => c.id === fixedCompanyId)?.displayName ??
      activeAssignment?.company?.displayName ??
      'Current Company'
    )
  }, [
    fixedCompanyId,
    companiesData?.data,
    activeAssignment?.company?.displayName,
  ])

  const createContact = useCreateContact()
  const updateContact = useUpdateContact()

  React.useEffect(() => {
    if (!open) {
      form.reset()
      setCompanySearch('')
    }
  }, [open, form])

  React.useEffect(() => {
    if (!open) return

    if (isEditMode && !contactData) {
      if (fixedCompanyId || initialEditCompanyId) {
        form.setValue(
          'companyId',
          fixedCompanyId ?? initialEditCompanyId ?? '',
          {
            shouldValidate: false,
          }
        )
      }

      if (initialEditTitle) {
        form.setValue('title', initialEditTitle, { shouldValidate: false })
      }
    }

    if (isEditMode && contactData) {
      form.reset({
        firstName: contactData.firstName ?? '',
        lastName: contactData.lastName ?? '',
        email: contactData.email ?? '',
        phone: contactData.phone ?? '',
        notes: contactData.notes ?? '',
        companyId:
          fixedCompanyId ??
          activeAssignment?.companyId ??
          initialEditCompanyId ??
          '',
        title: activeAssignment?.title ?? initialEditTitle ?? '',
      })
      return
    }

    if (!isEditMode && fixedCompanyId) {
      form.setValue('companyId', fixedCompanyId, { shouldValidate: true })
    }
  }, [
    open,
    isEditMode,
    contactData,
    activeAssignment,
    fixedCompanyId,
    initialEditCompanyId,
    initialEditTitle,
    form,
  ])

  const onSubmit = async (values: AddContactFormValues) => {
    try {
      const normalizedData = {
        firstName: values.firstName.trim(),
        lastName: values.lastName?.trim() || undefined,
        email: values.email?.trim() || undefined,
        phone: values.phone?.trim() || undefined,
        notes: values.notes?.trim() || undefined,
      }

      const selectedCompanyId = fixedCompanyId || values.companyId || undefined
      const selectedTitle = values.title
        ? (values.title as ContactTitle)
        : undefined

      if (isEditMode) {
        if (!contactId) throw new Error('Missing contact id')

        await updateContact.mutateAsync({
          id: contactId,
          data: {
            ...normalizedData,
            companyId: selectedCompanyId,
            title: selectedTitle,
          },
        })

        showSuccess('Contact updated successfully')
      } else {
        await createContact.mutateAsync({
          ...normalizedData,
          companyId: selectedCompanyId,
          title: selectedTitle,
          isPrimaryContact: Boolean(selectedCompanyId && forcePrimaryContact),
        })

        showSuccess('Contact created successfully')
      }

      onOpenChange(false)
      onSuccess?.()
    } catch {
      showError(
        isEditMode
          ? 'Failed to update contact. Please try again.'
          : 'Failed to create contact. Please try again.'
      )
    }
  }

  const isSubmitting = createContact.isPending || updateContact.isPending
  const isEditLoading = isEditMode && open && isLoadingContact

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-150 px-4 py-5')}>
        <DialogHeader>
          <DialogTitle className="text-balance">
            {isEditMode ? 'Edit Contact' : 'Add New Contact'}
          </DialogTitle>
          <DialogDescriptionHidden>
            {isEditMode
              ? 'Update contact details'
              : 'Fill out the form to create a new contact'}
          </DialogDescriptionHidden>
        </DialogHeader>

        {isEditLoading ? (
          <EditContactModalLoadingState />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-4">
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>
                          First Name
                          <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter first name"
                            autoComplete="given-name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter last name"
                            autoComplete="family-name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            autoComplete="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input
                            type="tel"
                            placeholder="Enter phone number"
                            autoComplete="tel"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="rounded-md border p-4">
                  <div className="flex gap-3">
                    {!fixedCompanyId ? (
                      <FormField
                        control={form.control}
                        name="companyId"
                        render={({ field }) => (
                          <FormItem className="flex flex-1 flex-col">
                            <FormLabel>Company</FormLabel>
                            <FormControl>
                              <SearchableSelect
                                options={
                                  companiesData?.data
                                    .filter((company) =>
                                      canEditCompany(
                                        company.primaryOwner?.id,
                                        company.secondaryOwner?.id
                                      )
                                    )
                                    .map((company) => ({
                                      value: company.id,
                                      label: company.displayName,
                                    })) ?? []
                                }
                                value={field.value}
                                onValueChange={field.onChange}
                                onSearchChange={setCompanySearch}
                                placeholder="Select Company"
                                searchPlaceholder="Search Company"
                                isLoading={companiesLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <div className="flex flex-1 flex-col gap-2">
                        <Label>Company</Label>
                        <Input value={fixedCompanyDisplayName} disabled />
                      </div>
                    )}

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem className="flex flex-1 flex-col">
                          <FormLabel>Job Title</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={!effectiveCompanyId}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select Job Title" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {JOB_TITLE_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Short Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter a short description"
                          rows={4}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline-muted"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || isLoadingContact}
                >
                  {isSubmitting
                    ? 'Saving...'
                    : isEditMode
                      ? 'Save Contact'
                      : 'Create Contact'}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function EditContactModalLoadingState() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-full" />
      </div>
      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
      <div className="rounded-md border p-4">
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-18" />
            <Skeleton className="h-9 w-full" />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-9 w-full" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-28 w-full" />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Skeleton className="h-9 w-20" />
        <Skeleton className="h-9 w-28" />
      </div>
    </div>
  )
}
